import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import type { ProfileMediaItem } from '../../services/mediaContracts';
import { fetchMediaBlob } from '../../services/zipDownload';
import {
  resolveTweetUrl,
  toPosterImageUrl,
  toThumbnailImageUrl,
  toViewerImageUrl,
} from '../../services/mediaUtils';
import TweetCaption from './TweetCaption';

const SWIPE_OFFSET_PX = 64;
const SWIPE_VELOCITY = 380;
const DISMISS_OFFSET_PX = 110;
const DISMISS_VELOCITY = 700;
/** Prefer lighter encodes on touch devices so first frame arrives sooner. */
const BITRATE_CAP_DESKTOP = 5_000_000;
const BITRATE_CAP_TOUCH = 2_500_000;

/** Transform-only slide — opacity/scale during swipe fights video decode on mobile. */
const slideVariants = {
  enter: (direction: number) => ({
    x: direction === 0 ? 0 : direction > 0 ? '100%' : '-100%',
    y: 0,
    opacity: 1,
  }),
  center: {
    x: 0,
    y: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction === 0 ? 0 : direction > 0 ? '-100%' : '100%',
    y: 0,
    opacity: 1,
  }),
};

const slideTransition = {
  x: { type: 'tween' as const, ease: [0.32, 0.72, 0, 1] as const, duration: 0.28 },
  y: { type: 'tween' as const, ease: [0.32, 0.72, 0, 1] as const, duration: 0.28 },
  opacity: { duration: 0.2 },
};

const reducedSlideTransition = {
  x: { type: 'tween' as const, duration: 0.01 },
  y: { type: 'tween' as const, duration: 0.01 },
  opacity: { duration: 0.01 },
};

const isTouchUi = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface MediaLightboxProps {
  open: boolean;
  items: ProfileMediaItem[];
  index: number;
  downloading?: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onDownload: (item: ProfileMediaItem) => void;
  onMentionClick?: (handle: string) => void;
}

const typeLabel: Record<ProfileMediaItem['type'], string> = {
  photo: 'Photo',
  video: 'Video',
  gif: 'GIF',
};

const formatDuration = (seconds?: number): string | null => {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const toHttps = (url: string): string =>
  url.startsWith('http:') ? url.replace(/^http:/, 'https:') : url;

/** Sized poster for video chrome — never pull `name=orig` behind a player. */
const posterUrl = (item: ProfileMediaItem): string => {
  const raw = item.thumbnailUrl || '';
  if (!raw) return '';
  return toHttps(toPosterImageUrl(raw) || toThumbnailImageUrl(raw) || raw);
};

const isProgressiveMp4 = (url: string): boolean =>
  Boolean(url) && url.includes('.mp4') && !url.includes('.m3u8');

/** Prefer a progressive MP4 under ~5 Mbps when variants exist (smoother in-browser play). */
const playbackUrlFor = (item: ProfileMediaItem): string => {
  const variants = item.variants;
  const candidates = (variants?.length ? variants : [{ url: item.downloadUrl }])
    .map((v) => ({ ...v, url: v.url ? toHttps(v.url) : '' }))
    .filter((v) => v.url && isProgressiveMp4(v.url))
    .sort((a, b) => (b.bitrate || 0) - (a.bitrate || 0));

  if (!candidates.length) {
    const fallback = toHttps(item.downloadUrl || '');
    return isProgressiveMp4(fallback) ? fallback : '';
  }

  const CAP = isTouchUi() ? BITRATE_CAP_TOUCH : BITRATE_CAP_DESKTOP;
  const suitable = candidates.find((v) => (v.bitrate || 0) > 0 && (v.bitrate || 0) <= CAP);
  return suitable?.url || candidates[0].url;
};

const waitForVideoEl = (
  getEl: () => HTMLVideoElement | null,
  signal: { cancelled: boolean }
): Promise<HTMLVideoElement | null> =>
  new Promise((resolve) => {
    let frames = 0;
    const tick = () => {
      if (signal.cancelled) {
        resolve(null);
        return;
      }
      const el = getEl();
      if (el) {
        resolve(el);
        return;
      }
      if (frames++ > 30) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });

const MediaLightbox: React.FC<MediaLightboxProps> = ({
  open,
  items,
  index,
  downloading,
  onClose,
  onNavigate,
  onDownload,
  onMentionClick,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wasPlayingBeforeDrag = useRef(false);
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const [needsPlayGesture, setNeedsPlayGesture] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  /** True once the video has painted a frame — keeps poster covering black decode. */
  const [videoReady, setVideoReady] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  /** Progressive photo: thumbnail first, then viewer-sized asset. */
  const [photoSrc, setPhotoSrc] = useState<string>('');
  /** 1 = next (swipe left), -1 = prev (swipe right), 0 = first open */
  const [slideDirection, setSlideDirection] = useState(0);
  const reduceMotion = prefersReducedMotion();

  const goTo = useCallback(
    (nextIndex: number) => {
      if (nextIndex === index || nextIndex < 0 || nextIndex >= items.length) return;
      setSlideDirection(nextIndex > index ? 1 : -1);
      onNavigate(nextIndex);
    },
    [index, items.length, onNavigate]
  );

  useEffect(() => {
    if (!open) {
      setSlideDirection(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) goTo(index - 1);
      if (e.key === 'ArrowRight' && hasNext) goTo(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, hasPrev, hasNext, onClose, goTo]);

  const handleDragStart = () => {
    const el = videoRef.current;
    wasPlayingBeforeDrag.current = Boolean(el && !el.paused && !el.ended);
    if (el && wasPlayingBeforeDrag.current) {
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    }
  };

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { offset, velocity } = info;
    const absX = Math.abs(offset.x);
    const absY = Math.abs(offset.y);

    // Swipe down to dismiss (dominant vertical gesture).
    if (
      absY > absX &&
      (offset.y > DISMISS_OFFSET_PX || velocity.y > DISMISS_VELOCITY)
    ) {
      wasPlayingBeforeDrag.current = false;
      onClose();
      return;
    }

    const swipedLeft = offset.x < -SWIPE_OFFSET_PX || velocity.x < -SWIPE_VELOCITY;
    const swipedRight = offset.x > SWIPE_OFFSET_PX || velocity.x > SWIPE_VELOCITY;

    if (swipedLeft && hasNext) {
      wasPlayingBeforeDrag.current = false;
      goTo(index + 1);
      return;
    }
    if (swipedRight && hasPrev) {
      wasPlayingBeforeDrag.current = false;
      goTo(index - 1);
      return;
    }

    // Snap-back: resume if playback was interrupted by the drag.
    if (wasPlayingBeforeDrag.current && videoRef.current) {
      void videoRef.current.play().catch(() => undefined);
    }
    wasPlayingBeforeDrag.current = false;
  };

  /**
   * Twimg 403s when the app origin is sent as Referer.
   * React can race `src` ahead of `referrerpolicy`, so we assign src imperatively
   * after no-referrer — and fall back to a no-referrer blob fetch if needed.
   */
  useEffect(() => {
    setNeedsPlayGesture(false);
    setIsMuted(true);
    setIsPlaying(false);
    setPlaybackError(null);
    setVideoLoading(false);
    setVideoReady(false);

    if (!open) return;
    if (item?.type !== 'video' && item?.type !== 'gif') return;

    const signal = { cancelled: false };
    let objectUrl: string | null = null;
    // Capture the element for cleanup — reading videoRef.current in the
    // cleanup callback trips react-hooks/exhaustive-deps (and fails Vercel CI).
    let activeEl: HTMLVideoElement | null = null;

    const attemptPlay = async (el: HTMLVideoElement) => {
      el.muted = true;
      try {
        await el.play();
        if (!signal.cancelled) {
          setIsPlaying(true);
          setNeedsPlayGesture(false);
          setVideoReady(true);
        }
      } catch {
        if (!signal.cancelled) {
          setIsPlaying(false);
          setNeedsPlayGesture(true);
          // Still show the first decoded frame under the play button.
          if (el.readyState >= 2) setVideoReady(true);
        }
      }
    };

    const loadDirect = (el: HTMLVideoElement, url: string, poster?: string) =>
      new Promise<void>((resolve, reject) => {
        const onOk = () => {
          cleanup();
          resolve();
        };
        const onErr = () => {
          cleanup();
          reject(new Error('direct media load failed'));
        };
        const cleanup = () => {
          el.removeEventListener('loadeddata', onOk);
          el.removeEventListener('canplay', onOk);
          el.removeEventListener('error', onErr);
        };

        // Order matters: policy + poster before src, then explicit load.
        el.setAttribute('referrerpolicy', 'no-referrer');
        if (poster) {
          el.setAttribute('poster', poster);
        } else {
          el.removeAttribute('poster');
        }
        el.preload = 'auto';
        el.src = url;
        el.load();
        el.addEventListener('loadeddata', onOk, { once: true });
        el.addEventListener('canplay', onOk, { once: true });
        el.addEventListener('error', onErr, { once: true });
      });

    const run = async () => {
      const remoteUrl = playbackUrlFor(item);
      if (!remoteUrl) {
        setPlaybackError('This clip isn’t available as a browser-playable MP4. Use Download.');
        return;
      }

      setVideoLoading(true);
      const el = await waitForVideoEl(() => videoRef.current, signal);
      if (!el || signal.cancelled) return;
      activeEl = el;

      try {
        try {
          await loadDirect(el, remoteUrl, posterUrl(item) || undefined);
        } catch {
          if (signal.cancelled) return;
          // Proven path from downloads: fetch without Referer, play from blob:.
          const blob = await fetchMediaBlob(remoteUrl);
          if (signal.cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          el.setAttribute('referrerpolicy', 'no-referrer');
          const poster = posterUrl(item);
          if (poster) el.setAttribute('poster', poster);
          el.src = objectUrl;
          el.load();
          await new Promise<void>((resolve, reject) => {
            const onOk = () => {
              cleanup();
              resolve();
            };
            const onErr = () => {
              cleanup();
              reject(new Error('blob media load failed'));
            };
            const cleanup = () => {
              el.removeEventListener('loadeddata', onOk);
              el.removeEventListener('canplay', onOk);
              el.removeEventListener('error', onErr);
            };
            el.addEventListener('loadeddata', onOk, { once: true });
            el.addEventListener('canplay', onOk, { once: true });
            el.addEventListener('error', onErr, { once: true });
          });
        }

        if (signal.cancelled) return;
        setVideoLoading(false);
        if (el.readyState >= 2) setVideoReady(true);
        await attemptPlay(el);
      } catch {
        if (!signal.cancelled) {
          setVideoLoading(false);
          setPlaybackError('Couldn’t load this video. Try Download instead.');
          el.pause();
        }
      }
    };

    void run();

    return () => {
      signal.cancelled = true;
      // Pause only — clearing src mid-exit paints black and causes swipe flicker.
      if (activeEl) {
        try {
          activeEl.pause();
        } catch {
          /* ignore */
        }
      }
      if (objectUrl) {
        const revoke = objectUrl;
        // Defer revoke so the exiting slide can finish its transform.
        window.setTimeout(() => URL.revokeObjectURL(revoke), 400);
      }
    };
  }, [open, index, item]);

  /** Warm adjacent media so the next swipe paints immediately. */
  useEffect(() => {
    if (!open) return;

    const neighbors = [items[index - 1], items[index + 1]].filter(
      (n): n is ProfileMediaItem => Boolean(n)
    );
    const linkNodes: HTMLLinkElement[] = [];
    const touch = isTouchUi();

    for (const neighbor of neighbors) {
      // Always warm the poster/preview — instant paint when landing on video/gif.
      const neighborPoster = posterUrl(neighbor);
      if (neighborPoster) {
        const img = new Image();
        img.referrerPolicy = 'no-referrer';
        img.src = neighborPoster;
      }

      if (neighbor.type === 'photo') {
        const viewerUrl = toViewerImageUrl(
          neighbor.downloadUrl || neighbor.thumbnailUrl,
          touch
        );
        if (!viewerUrl) continue;
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'image';
        link.href = toHttps(viewerUrl);
        link.setAttribute('referrerpolicy', 'no-referrer');
        document.head.appendChild(link);
        linkNodes.push(link);
      }
    }

    return () => {
      linkNodes.forEach((n) => n.remove());
    };
  }, [open, index, items]);

  /** Progressive photo: show thumbnail immediately, then upgrade to viewer size. */
  useEffect(() => {
    if (!open || !item || item.type !== 'photo') {
      setPhotoSrc('');
      return;
    }

    const touch = isTouchUi();
    const thumb = item.thumbnailUrl
      ? toHttps(toThumbnailImageUrl(item.thumbnailUrl) || item.thumbnailUrl)
      : '';
    const full = toHttps(
      toViewerImageUrl(item.downloadUrl || item.thumbnailUrl, touch)
    );
    setPhotoSrc(thumb || full);
    if (!full || full === thumb) return;

    let cancelled = false;
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => {
      if (!cancelled) setPhotoSrc(full);
    };
    img.src = full;
    return () => {
      cancelled = true;
    };
  }, [open, index, item]);

  if (!item) return null;

  const isGif = item.type === 'gif';
  const isVideo = item.type === 'video' || isGif;
  const poster = isVideo ? posterUrl(item) : '';
  const duration = formatDuration(item.duration);
  const postUrl = resolveTweetUrl(item);

  const handlePlayClick = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = isGif ? true : isMuted;
    el.play()
      .then(() => {
        setIsPlaying(true);
        setNeedsPlayGesture(false);
      })
      .catch(() => {
        setNeedsPlayGesture(true);
      });
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el || isGif) return;
    const next = !isMuted;
    el.muted = next;
    setIsMuted(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.96)',
          backgroundImage: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            px: { xs: 1.5, sm: 2 },
            pt: 'max(12px, env(safe-area-inset-top))',
            pb: 1.5,
            borderBottom: '1px solid #2F3336',
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close viewer"
            sx={{ color: '#E7E9EA' }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ color: '#E7E9EA', fontWeight: 600 }} noWrap>
              {typeLabel[item.type]}
              {item.author ? ` · @${item.author}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {index + 1} of {items.length}
              {duration ? ` · ${duration}` : ''}
              {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
            </Typography>
          </Box>
          {postUrl && (
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', color: '#1D9BF0', display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Thread
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={downloading}
            startIcon={
              downloading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CloudDownloadIcon />
              )
            }
            onClick={() => onDownload(item)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Download
          </Button>
        </Box>

        {/* Media stage */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 1, sm: 8 },
            py: 2,
            overflow: 'hidden',
            touchAction: 'none',
          }}
        >
          {hasPrev && (
            <IconButton
              onClick={() => goTo(index - 1)}
              aria-label="Previous media"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                position: 'absolute',
                left: { xs: 8, sm: 16 },
                zIndex: 2,
                p: { xs: 0.75, sm: 1 },
                color: '#E7E9EA',
                backgroundColor: 'rgba(47, 51, 54, 0.8)',
                '&:hover': { backgroundColor: 'rgba(47, 51, 54, 1)' },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: { xs: 26, sm: 35 } }} />
            </IconButton>
          )}

          <AnimatePresence initial={false} custom={slideDirection}>
            <Box
              component={motion.div}
              key={`${item.tweetId}-${item.downloadUrl}-${index}`}
              custom={slideDirection}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={reduceMotion ? reducedSlideTransition : slideTransition}
              drag
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              dragElastic={0.18}
              dragDirectionLock
              dragMomentum={false}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                willChange: 'transform',
              }}
              sx={{
                px: { xs: 1, sm: 0 },
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              {isVideo ? (
                <>
                  {/* Poster stays painted until the video has a real frame — kills black flash. */}
                  {poster && !videoReady && !playbackError && (
                    <Box
                      component="img"
                      src={poster}
                      alt=""
                      referrerPolicy="no-referrer"
                      draggable={false}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        m: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: 1,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    />
                  )}

                  <video
                    ref={videoRef}
                    controls={!isGif && !playbackError && videoReady}
                    playsInline
                    muted
                    loop={isGif}
                    preload="auto"
                    poster={poster || undefined}
                    onLoadedData={() => setVideoReady(true)}
                    onPlaying={() => {
                      setIsPlaying(true);
                      setVideoReady(true);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onClick={needsPlayGesture ? handlePlayClick : undefined}
                    style={{
                      display: playbackError ? 'none' : 'block',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      minHeight: 160,
                      objectFit: 'contain',
                      borderRadius: 4,
                      outline: 'none',
                      backgroundColor: '#000',
                      cursor: needsPlayGesture ? 'pointer' : 'default',
                      opacity: videoReady ? 1 : 0,
                      transition: 'opacity 0.15s ease-out',
                      position: 'relative',
                      zIndex: 0,
                    }}
                  />

                  {videoLoading && !playbackError && !videoReady && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        pointerEvents: 'none',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: 'rgba(231, 233, 234, 0.85)' }} />
                    </Box>
                  )}

                  {playbackError && (
                    <Box
                      sx={{
                        maxWidth: 360,
                        px: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#E7E9EA', mb: 2 }}>
                        {playbackError}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CloudDownloadIcon />}
                        onClick={() => onDownload(item)}
                        disabled={downloading}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Download
                      </Button>
                    </Box>
                  )}

                  {/* Autoplay blocked — clear play affordance */}
                  <AnimatePresence>
                    {needsPlayGesture && !playbackError && !videoLoading && (
                      <Box
                        component={motion.button}
                        type="button"
                        aria-label="Play media"
                        onClick={handlePlayClick}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.96 }}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          m: 'auto',
                          width: 72,
                          height: 72,
                          border: '1px solid #2F3336',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(15, 20, 25, 0.72)',
                          color: '#E7E9EA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          zIndex: 3,
                          backdropFilter: 'blur(6px)',
                          p: 0,
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 40, ml: 0.5 }} />
                      </Box>
                    )}
                  </AnimatePresence>

                  {/* Unmute chip for videos (GIFs stay muted) */}
                  {!isGif && isPlaying && !needsPlayGesture && !playbackError && videoReady && (
                    <Box
                      component={motion.button}
                      type="button"
                      aria-label={isMuted ? 'Unmute' : 'Mute'}
                      onClick={toggleMute}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22, ease: 'easeOut', delay: 0.15 }}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      sx={{
                        position: 'absolute',
                        bottom: { xs: 16, sm: 24 },
                        right: { xs: 16, sm: 24 },
                        zIndex: 3,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        border: '1px solid #2F3336',
                        backgroundColor: 'rgba(15, 20, 25, 0.78)',
                        color: '#E7E9EA',
                        cursor: 'pointer',
                        backdropFilter: 'blur(6px)',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                      }}
                    >
                      {isMuted ? (
                        <VolumeOffIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <VolumeUpIcon sx={{ fontSize: 18 }} />
                      )}
                      {isMuted ? 'Unmute' : 'Mute'}
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  component="img"
                  src={photoSrc || toHttps(item.thumbnailUrl || item.downloadUrl)}
                  alt={item.text?.slice(0, 120) || 'Media'}
                  referrerPolicy="no-referrer"
                  decoding="async"
                  draggable={false}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    minHeight: { xs: 120, sm: 160 },
                    objectFit: 'contain',
                    borderRadius: 1,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                  }}
                />
              )}
            </Box>
          </AnimatePresence>

          {hasNext && (
            <IconButton
              onClick={() => goTo(index + 1)}
              aria-label="Next media"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                position: 'absolute',
                right: { xs: 8, sm: 16 },
                zIndex: 2,
                p: { xs: 0.75, sm: 1 },
                color: '#E7E9EA',
                backgroundColor: 'rgba(47, 51, 54, 0.8)',
                '&:hover': { backgroundColor: 'rgba(47, 51, 54, 1)' },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: { xs: 26, sm: 35 } }} />
            </IconButton>
          )}
        </Box>

        {/* Tweet caption */}
        {(item.text || item.author) && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: '1px solid #2F3336',
              backgroundColor: '#16181C',
              px: { xs: 2, sm: 3 },
              pt: 2,
              pb: {
                xs: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                sm: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
              },
              maxHeight: { xs: '46vh', sm: '42vh' },
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            <Box key={`caption-${item.tweetId}-${index}`}>
              <TweetCaption item={item} compact onMentionClick={onMentionClick} />
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default MediaLightbox;
